import { useEffect, useRef, type MouseEvent, type RefObject } from 'react';

/**
 * A menu or panel closes on a click outside it and on Escape (focus back to its button). Its own
 * button is left out: that one toggles. Listens in the document the menu lives in, so menus in the
 * pop-out call window work too.
 */
export function useDismiss(open: boolean, close: () => void, panel: RefObject<HTMLElement | null>, button?: RefObject<HTMLElement | null>) {
  const latest = useRef(close);
  latest.current = close;
  useEffect(() => {
    if (!open) return;
    const doc = panel.current?.ownerDocument ?? document;
    const down = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || button?.current?.contains(target)) return;
      latest.current();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      latest.current();
      button?.current?.focus();
    };
    doc.addEventListener('pointerdown', down);
    doc.addEventListener('keydown', key);
    return () => {
      doc.removeEventListener('pointerdown', down);
      doc.removeEventListener('keydown', key);
    };
  }, [open, panel, button]);
}

/** A modal <dialog> closes on a click on the dimmed backdrop, as on Escape (its padding does not count). */
export function closeOnBackdrop(event: MouseEvent<HTMLDialogElement>) {
  const dialog = event.currentTarget;
  if (event.target !== dialog) return;
  const box = dialog.getBoundingClientRect();
  if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
}
